import { BadRequest } from '@tsed/exceptions';
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { getHomeBannerFn } from '@/api/cms/home-banner/useGetHomeBanner';
import type { AvailableCity } from '@/api/service/useGetAvailableCities';
import { getAvailableCitiesFn } from '@/api/service/useGetAvailableCities';
import { getServiceModelsFn } from '@/api/service/useGetServiceModels';
import apiRoutes from '@/lib/apiRoutes';
import getIsSsrMobile from '@/lib/getIsSsrMobile';
import { queryClient } from '@/lib/queryClient';
import { homeConfig } from '@/modules/home/homeConfig';
import HomePage from '@/modules/home/HomePage';
import type { HelpCenter } from '../api/cms/help-center/types';
import { getHelpCenterFn } from '../api/cms/help-center/useGetHelpCenter';
import type { BannerItem, DefaultCmsDataResponse } from '../api/cms/types';

export type HomeProps = {
  // Declare Home props
  helpCenter: DefaultCmsDataResponse<HelpCenter> | null;
  availableCities: AvailableCity[];
  isSsrMobile: boolean;
  homeBannerRes: BannerItem[] | null;
};

export default function Home(
  _props: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { query, pathname, replace } = useRouter();

  if (query.id) {
    const temp = {
      ...query,
    };
    delete temp.id;
    replace(
      {
        pathname,
        query: {
          ...temp,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );
  }
  return <HomePage {..._props} />;
}

export const getServerSideProps: GetServerSideProps<HomeProps> = async (
  context
) => {
  const { locale } = context;
  if (locale == undefined) {
    throw new BadRequest('locale is missing');
  }

  const { i18nNamespaces } = homeConfig;

  let helpCenterRes: DefaultCmsDataResponse<HelpCenter>[] = [];
  let availableCitiesRes: string[] = [];

  let homeBannerRes = [] as BannerItem[];
  try {
    const res = await getHomeBannerFn();
    homeBannerRes =
      (res && res.data && res.data.attributes && res.data.attributes.items) ||
      [];
  } catch (error) {
    console.log('HomeBanner', (error as Error).message);
  }

  try {
    availableCitiesRes = await getAvailableCitiesFn();
  } catch (error) {
    console.log(error);
  }

  await queryClient.prefetchQuery([apiRoutes.services.service_models], () =>
    getServiceModelsFn({
      city_id: 'DAD',
    })
  );

  try {
    await getHelpCenterFn({
      type: 'ahafast',
      locale: locale === 'en' ? 'en' : 'vi-VN',
    })
      .then((res) => {
        helpCenterRes = res.data;
      })
      .catch((error) => console.log(error));
  } catch (error) {
    console.log(error);
  }

  const isSsrMobile = getIsSsrMobile(context);

  return {
    props: {
      ...(await serverSideTranslations(locale, i18nNamespaces)),
      helpCenter: helpCenterRes.length > 0 ? helpCenterRes[0] : null,
      availableCities: availableCitiesRes,
      isSsrMobile,
      homeBannerRes,
    },
  };
};
